import React, { useEffect, useMemo, useState } from "react";

import { Row, Col, Card, Select, Typography, Tag, Space, Empty } from "antd";

import {
  GoogleMap,
  Marker,
  Polyline,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";

import { useDispatch, useSelector } from "react-redux";

import { getLiveLocationsApi } from "../../../apiservice/hrpayroll";

import { GOOGLE_MAP_LIBRARIES } from "../../../constants/googleMaps";

import { fetchRouteAssignments } from "../workRoutesThunk";

const { Text } = Typography;

const libraries = GOOGLE_MAP_LIBRARIES;

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 220px)",
};

const REFRESH_INTERVAL = 30000;

const LiveTracking = ({ employees = [], center, zoom }) => {
  const dispatch = useDispatch();

  const { routeAssignments, workRoutes } = useSelector(
    (state) => state.workRoutes,
  );

  const defaultCenter = center || {
    lat: 20,
    lng: 0,
  };

  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries,
  });

  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  const [liveLocations, setLiveLocations] = useState([]);

  const selectedAssignment = useMemo(() => {
    return routeAssignments.find((a) => a.id === selectedAssignmentId);
  }, [selectedAssignmentId, routeAssignments]);

  const selectedRoute = useMemo(() => {
    if (!selectedAssignment) return null;

    return workRoutes.find((r) => r.id === selectedAssignment.route_id);
  }, [selectedAssignment, workRoutes]);

  const selectedRoutePoints = useMemo(() => {
    if (!selectedRoute?.points) return [];

    return selectedRoute.points;
  }, [selectedRoute]);

  useEffect(() => {
    dispatch(fetchRouteAssignments());
  }, [dispatch]);

  const fetchLiveLocations = async () => {
    try {
      const res = await getLiveLocationsApi({
        assignment_id: selectedAssignment.id,
      });

      setLiveLocations(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!selectedAssignment) return;

    fetchLiveLocations();

    const interval = setInterval(() => {
      fetchLiveLocations();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [selectedAssignment]);

  const latestLocation = useMemo(() => {
    if (!liveLocations.length) return null;

    return liveLocations[liveLocations.length - 1];
  }, [liveLocations]);

  const livePath = useMemo(() => {
    return liveLocations.map((loc) => ({
      lat: Number(loc.latitude),

      lng: Number(loc.longitude),
    }));
  }, [liveLocations]);

  // STATIC ROUTE PATH
  // NO GOOGLE DIRECTIONS API
  const routePath = useMemo(() => {
    return selectedRoutePoints.map((point) => ({
      lat: Number(point.latitude),

      lng: Number(point.longitude),
    }));
  }, [selectedRoutePoints]);

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <Card title="Live Tracking">
            <Space
              direction="vertical"
              style={{
                width: "100%",
              }}
            >
              <div>
                <Text strong>Active Assignment</Text>

                <Select
                  style={{
                    width: "100%",
                    marginTop: 8,
                  }}
                  placeholder="Select Assignment"
                  value={selectedAssignmentId}
                  onChange={setSelectedAssignmentId}
                  options={routeAssignments.map((assignment) => ({
                    label: `${assignment.employee_name} - ${assignment.route_name}`,

                    value: assignment.id,
                  }))}
                />
              </div>

              {latestLocation && (
                <Card size="small">
                  <Space direction="vertical">
                    <Text>Latitude: {latestLocation.latitude}</Text>

                    <Text>Longitude: {latestLocation.longitude}</Text>

                    <Text>Accuracy: {latestLocation.accuracy_meters}m</Text>

                    <Tag color="green">GPS Active</Tag>
                  </Space>
                </Card>
              )}

              {selectedRoutePoints.map((point, index) => (
                <Card key={point.id} size="small">
                  <Space direction="vertical">
                    <Text strong>
                      {index + 1}. {point.name}
                    </Text>

                    <Tag color="blue">Route Point</Tag>
                  </Space>
                </Card>
              ))}
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={18}>
          <Card title="Live Route Map">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={
                  latestLocation
                    ? {
                        lat: Number(latestLocation.latitude),

                        lng: Number(latestLocation.longitude),
                      }
                    : selectedRoutePoints.length
                      ? {
                          lat: Number(selectedRoutePoints[0].latitude),

                          lng: Number(selectedRoutePoints[0].longitude),
                        }
                      : defaultCenter
                }
                zoom={
                  latestLocation || selectedRoutePoints.length ? 14 : zoom || 2
                }
              >
                {/* ROUTE PATH */}
                {!!routePath.length && (
                  <Polyline
                    path={routePath}
                    options={{
                      strokeColor: "#1890ff",

                      strokeOpacity: 1,

                      strokeWeight: 4,
                    }}
                  />
                )}

                {/* ROUTE POINTS */}
                {selectedRoutePoints.map((point, index) => (
                  <React.Fragment key={point.id}>
                    <Marker
                      label={`${index + 1}`}
                      position={{
                        lat: Number(point.latitude),

                        lng: Number(point.longitude),
                      }}
                    />

                    <Circle
                      center={{
                        lat: Number(point.latitude),

                        lng: Number(point.longitude),
                      }}
                      radius={Number(point.radius_meters || 100)}
                      options={{
                        fillColor: "#1890ff",

                        fillOpacity: 0.2,

                        strokeColor: "#1890ff",
                      }}
                    />
                  </React.Fragment>
                ))}

                {/* LIVE EMPLOYEE LOCATION */}
                {latestLocation && (
                  <Marker
                    position={{
                      lat: Number(latestLocation.latitude),

                      lng: Number(latestLocation.longitude),
                    }}
                  />
                )}

                {/* LIVE MOVEMENT PATH */}
                {!!livePath.length && (
                  <Polyline
                    path={livePath}
                    options={{
                      strokeColor: "#ff4d4f",

                      strokeOpacity: 1,

                      strokeWeight: 4,
                    }}
                  />
                )}
              </GoogleMap>
            ) : (
              <Empty description="Loading Map" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LiveTracking;
